import { useCallback, useEffect, useState } from "react";
import { CONSENT_STORAGE_KEY } from "../constants/cookieConsent";

const CONSENT_CHANGED_EVENT = "leela-cookie-consent-changed";

const readStoredConsent = () => {
    try {
        const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
};

// Shared consent state across all mounted components (banner + settings modal +
// any consumer gating a script), backed by localStorage and a same-tab custom
// event so every hook instance stays in sync without a context provider.
export const useCookieConsent = () => {
    const [consent, setConsentState] = useState(readStoredConsent);

    useEffect(() => {
        const onChange = () => setConsentState(readStoredConsent());
        window.addEventListener(CONSENT_CHANGED_EVENT, onChange);
        window.addEventListener("storage", onChange);
        return () => {
            window.removeEventListener(CONSENT_CHANGED_EVENT, onChange);
            window.removeEventListener("storage", onChange);
        };
    }, []);

    const saveConsent = useCallback((analyticsAllowed) => {
        const value = {
            analytics: !!analyticsAllowed,
            decidedAt: new Date().toISOString(),
        };
        localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(value));
        window.dispatchEvent(new Event(CONSENT_CHANGED_EVENT));
    }, []);

    const acceptAll = useCallback(() => saveConsent(true), [saveConsent]);
    const rejectOptional = useCallback(() => saveConsent(false), [saveConsent]);

    return {
        hasDecided: consent !== null,
        analyticsAllowed: !!consent?.analytics,
        acceptAll,
        rejectOptional,
        saveConsent,
    };
};
